import { Store } from "@tauri-apps/plugin-store";
import type { Conversation, ConversationSummary } from "@/types";
import type { ConversationRepository } from "./ConversationRepository";
import { toConversationSummary } from "./summarize";

/**
 * Tauri-store-backed conversation repository.
 *
 * Persists to a JSON file in the app data directory via
 * `@tauri-apps/plugin-store`. Each conversation is stored under its own key
 * plus a small summary index for cheap listing, so opening the sidebar never
 * has to load every conversation's full message history.
 */
const STORE_FILE = "conversations.json";
const INDEX_KEY = "index";
const conversationKey = (id: string) => `conversation:${id}`;

let storePromise: Promise<Store> | null = null;
function getStore(): Promise<Store> {
  storePromise ??= Store.load(STORE_FILE);
  return storePromise;
}

export const tauriStoreConversationRepository: ConversationRepository = {
  async list() {
    const store = await getStore();
    const index = (await store.get<ConversationSummary[]>(INDEX_KEY)) ?? [];
    return [...index].sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async get(id) {
    const store = await getStore();
    return (await store.get<Conversation>(conversationKey(id))) ?? null;
  },

  async save(conversation) {
    const store = await getStore();
    await store.set(conversationKey(conversation.id), conversation);
    const index = (await store.get<ConversationSummary[]>(INDEX_KEY)) ?? [];
    const next = index.filter((s) => s.id !== conversation.id);
    next.push(toConversationSummary(conversation));
    await store.set(INDEX_KEY, next);
    await store.save();
  },

  async delete(id) {
    const store = await getStore();
    await store.delete(conversationKey(id));
    const index = (await store.get<ConversationSummary[]>(INDEX_KEY)) ?? [];
    await store.set(
      INDEX_KEY,
      index.filter((s) => s.id !== id),
    );
    await store.save();
  },
};
